import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { EstimationUserAssignmentRequest } from '../../schema/data-contracts';
import { getEstimationQueryKey, getEstimationsQueryKey } from '../estimationQueryActions';
import type { MutationProps } from '../types';

const getEstimationLevelPermissionsQueryKey = 'get-estimation-permissions';

export const useEstimationUserAssignmentQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationUserAssignmentController: { v1EstimationsPermissionsList, v1EstimationsUsersUpdate },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetEstimationPermissions = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationId,
      queryKey: [getEstimationLevelPermissionsQueryKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsPermissionsList(estimationId!);
        return data;
      },
    });

  const useUpdateUserAssignments = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['assign-users'],
      mutationFn: async ({
        estimationId,
        estimationUserAssignments,
      }: {
        estimationId: string;
        estimationUserAssignments: EstimationUserAssignmentRequest[];
      }) => {
        const { data } = await v1EstimationsUsersUpdate(estimationId, estimationUserAssignments);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === getEstimationsQueryKey ||
            query.queryKey[0] === getEstimationLevelPermissionsQueryKey ||
            (query.queryKey[0] === 'estimation-users' &&
              query.queryKey[1] === variables.estimationId) ||
            (query.queryKey[0] === getEstimationQueryKey &&
              query.queryKey[1] === variables.estimationId),
        });
      },
    });

  return { useGetEstimationPermissions, useUpdateUserAssignments };
};
